import { useContext } from 'react';
import { useTranslation } from 'react-i18next';

import List from '@pelckmans/elementis/components/list';
import ListGroup from '@pelckmans/elementis/components/list-group';
import Pill from '@pelckmans/elementis/components/pill';
import PillGroup from '@pelckmans/elementis/components/pill-group';
import Section from '@pelckmans/elementis/components/section';
import FailureIcon from '@pelckmans/elementis/icons/failure';
import PelckmansIcon from '@pelckmans/elementis/icons/pelckmans';
import PendingIcon from '@pelckmans/elementis/icons/pending';
import SmartschoolIcon from '@pelckmans/elementis/icons/smartschool';
import SuccessIcon from '@pelckmans/elementis/icons/success';

import ModalContext from '../../../contexts/ModalContext';
import useGroups, { SchoolYear } from '../hooks/useGroups';
import GroupDetailsModal from '../modals/group-details/GroupDetailsModal';
import AddGroupButton from './AddGroupButton';
import GroupsEmptyState from './GroupsEmptyState';
import LoadingState from '../../../shared-components/LoadingState';
import { Group } from '../../../api/groups';

const renderSchoolNameWithCities = (school: Group['school']) => {
  if (school.cities) {
    return `${school.name}, ${school.cities}`;
  }

  return school.name;
};

type StudentStatusPillsProps = {
  group: Group;
};

function StudentStatusPills({ group }: StudentStatusPillsProps): JSX.Element {
  const { t } = useTranslation();
  const pills = [];

  if (group.totalAccepted > 0) {
    pills.push(
      <Pill
        key="accepted"
        icon={<SuccessIcon data-testid="success-icon" />}
        prominence="success"
        aria-label={t('PAGES.MY_GROUPS.LIST_GROUP.STUDENTS.ACCEPTED.A11Y_LABEL', {
          amount: group.totalAccepted,
        })}
      >
        {String(group.totalAccepted)}
      </Pill>,
    );
  }

  if (group.totalPending > 0) {
    pills.push(
      <Pill
        key="pending"
        icon={<PendingIcon data-testid="pending-icon" />}
        prominence="warning"
        aria-label={t('PAGES.MY_GROUPS.LIST_GROUP.STUDENTS.PENDING.A11Y_LABEL', {
          amount: group.totalPending,
        })}
      >
        {String(group.totalPending)}
      </Pill>,
    );
  }

  if (group.totalDeclined > 0) {
    pills.push(
      <Pill
        key="declined"
        icon={<FailureIcon data-testid="failure-icon" />}
        prominence="danger"
        aria-label={t('PAGES.MY_GROUPS.LIST_GROUP.STUDENTS.DECLINED.A11Y_LABEL', {
          amount: group.totalDeclined,
        })}
      >
        {String(group.totalDeclined)}
      </Pill>,
    );
  }

  return pills.length === 0 ? (
    <></>
  ) : (
    <PillGroup role="group" aria-label={t('PAGES.MY_GROUPS.LIST_GROUP.STUDENTS.A11Y_LABEL')} size="sm">
      {pills}
    </PillGroup>
  );
}

type Props = {
  schoolYear: SchoolYear;
};

function OverviewMyGroups({ schoolYear }: Props) {
  const { groupsWithSchool, selectedSchoolYear, onGroupsChange, isLoadingGroups } = useGroups();
  const { t } = useTranslation();
  const { showModal } = useContext(ModalContext);

  const addGroups = !schoolYear.hasPassed;

  const addGroupToState = (group: Group) => {
    onGroupsChange(prevGroups => {
      return (prevGroups || []).map(g => {
        if (g.id === group.id) {
          return group;
        }

        return g;
      });
    });
  };

  const removeGroupFromState = (group: Group) => {
    onGroupsChange(prevGroups => {
      return (prevGroups || []).filter(g => g.id !== group.id);
    });
  };

  if (!selectedSchoolYear) return null;

  return (
    <Section
      title={t('PAGES.MY_GROUPS.SECTION_TITLE', {
        schoolYearStartYear: schoolYear.startYear,
        schoolYearEndYear: schoolYear.endYear,
      })}
      actions={
        !isLoadingGroups && addGroups && groupsWithSchool.length > 0 ? (
          <AddGroupButton onGroupsChange={onGroupsChange} schoolYear={selectedSchoolYear} size="sm" />
        ) : undefined
      }
      actionGroupRole={undefined}
      actionGroupAriaLabel={undefined}
    >
      <div id="my-groups-overview">
        {isLoadingGroups && (
          <LoadingState variant="list" aria-label={t('PAGES.MY_GROUPS.LIST_GROUP.LOADING.A11Y_LABEL')} />
        )}

        {!isLoadingGroups && (
          <>
            {groupsWithSchool.length === 0 && <GroupsEmptyState schoolYear={selectedSchoolYear} />}

            <ListGroup>
              {groupsWithSchool.map(({ school, groups }) => {
                return (
                  <List key={school.id}>
                    <List.Header title={renderSchoolNameWithCities(school)} prominent titleTag="h3" />

                    {groups.map(group => (
                      <List.Item
                        key={group.id}
                        title={group.name}
                        icon={group.smartschoolGroupId ? <SmartschoolIcon /> : <PelckmansIcon />}
                        onClick={() => {
                          if (!selectedSchoolYear) return;

                          showModal(
                            <GroupDetailsModal
                              id="group-details-modal"
                              group={group}
                              schoolYear={selectedSchoolYear}
                              editable={!selectedSchoolYear?.hasPassed}
                              onGroupRename={addGroupToState}
                              onGroupDelete={removeGroupFromState}
                            />,
                          );
                        }}
                        as="button"
                        actionArea={<StudentStatusPills group={group} />}
                        pillGroupProps={{
                          role: 'group',
                          'aria-label': t('PAGES.MY_GROUPS.LIST_GROUP.SUBJECTS.A11Y_LABEL'),
                          children: group.subjects.map((s, i) => <Pill key={`${i}-${s}`}>{s}</Pill>),
                        }}
                      />
                    ))}
                  </List>
                );
              })}
            </ListGroup>
          </>
        )}
      </div>
    </Section>
  );
}

export default OverviewMyGroups;
